import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import Label from '~components/FormFields/v2/Label';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { Order } from '~hooks/useOrders/models';

import { OrderTrucksCountDto, orderTrucksCountSchema } from './orderTrucksCountSchema';

export type OrderTrucksCountFormHandler = {
  onSubmit: () => Promise<OrderTrucksCountDto | undefined>;
};

interface OrderTrucksCountFormProps {
  isSubmitting?: boolean;
  order: Order;
}

function resolveInitialTrucksCount(order: Order): number | '' {
  const jobsCount = order.orderSummary?.jobsCount;
  return jobsCount ? jobsCount : (order.truckCount ?? '');
}

const OrderTrucksCountForm = forwardRef<
  OrderTrucksCountFormHandler,
  OrderTrucksCountFormProps
>(function OrderTrucksCountForm({ isSubmitting, order }, ref) {
  const trucksCount = resolveInitialTrucksCount(order);

  const form = useForm<OrderTrucksCountDto>({
    resolver: yupResolver(orderTrucksCountSchema),
    mode: 'onChange',
    defaultValues: { trucksCount: trucksCount as number },
  });

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          form.handleSubmit(
            (data) => resolve(data),
            () => resolve(undefined),
          )();
        });
      },
    }),
    [form.handleSubmit],
  );

  useEffect(() => {
    form.reset({ trucksCount: trucksCount as number });
  }, [form.reset, trucksCount]);

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Label sx={{ whiteSpace: 'nowrap', mt: '7px', fontSize: '12px' }}>
        {t('account.form.truck_count')}
      </Label>

      <NumberInput
        control={form.control}
        slotProps={{
          htmlInput: { sx: { fontSize: '12px', width: '40px', py: '6.5px' } },
        }}
        isDisabled={isSubmitting}
        isRequired
        name="trucksCount"
        suppressErrorMessage
      />
    </Box>
  );
});

export default OrderTrucksCountForm;
